import OncolensToast from '@/components/OncolensToast';
import { LegacyAdminRoutes } from '@/utils/legacyRouteResolver';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import Checkbox from '@oncolens/oncolens-ui/lib/components/FormControls/Checkbox';
import Listbox, { SelectItem } from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import TextAreaInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextAreaInput';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import { Tooltip } from '@oncolens/oncolens-ui/lib/components/FormControls/Tooltip';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import useFormStore from '@oncolens/oncolens-ui/lib/libraries/useFormStore';
import React, { useEffect, useState } from 'react';
import { FaArrowRight, FaInfoCircle } from 'react-icons/fa';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useUserManager } from '@/hooks/featureFlags/active-flags/useUserManager';
import { useV2ConferenceEmails } from '@/hooks/featureFlags/active-flags/useV2ConferenceEmails';
import { XIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import shallow from 'zustand/shallow';
import { useGetEffectiveRegion } from '../api/getEffectiveRegion';
import { useOrganizationHasLicense } from '../api/getOrgHasLicense';
import { useGetOrganization } from '../api/getOrganization';
import { getLegacyOrganizationId } from '../api/organizations';
import { useUpdateOrganization } from '../api/updateOrganization';
import {
  Licenses,
  OncoLensRootOrganizationId,
  OrganizationDto,
  OrganizationForUpdateDto,
  OrganizationRegions,
} from '../types';
import { OrganizationLogoUpload } from './OrganizationLogoUpload';

const regionOptions: SelectItem[] = [
  { label: 'US', value: OrganizationRegions.US },
  { label: 'Non-US', value: OrganizationRegions.NonUS },
];

type LegacyOrgLinkProps = {
  organization: OrganizationDto;
};

const LegacyOrgLink = ({ organization }: LegacyOrgLinkProps) => {
  const handleLegacyRedirect = () => {
    getLegacyOrganizationId(organization.id)
      .then((lookup) => {
        const url = LegacyAdminRoutes.hospital.edit(lookup.legacyId);

        window.open(url);
      })
      .catch(() => {
        // eslint-disable-next-line no-alert
        alert(
          `'${organization.name}' isn't currently linked to a hospital or facility in the legacy system.`,
        );
      });
  };

  return (
    <Button
      onClick={handleLegacyRedirect}
      buttonColor="blue"
      buttonStyle="primary"
      className="inline-flex items-center w-auto py-2 mr-3">
      Edit (v1) <FaArrowRight className="ml-2" />
    </Button>
  );
};

type OrganizationFormProps = {
  organizationId: string | undefined;
};

// Mirror the server-side regexes in Administration's Organization.ValidateBrandingFormat.
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

// Fallback for the native color swatch, which cannot represent "unset" — it always renders
// some color. Shown only while brandColor is empty or mid-edit (e.g. "#1a7").
const UNSET_SWATCH_COLOR = '#000000';

const organizationSchema = yup.object<Record<keyof OrganizationForUpdateDto, yup.AnySchema>>({
  name: yup.string().required(),
  referenceCode: yup.string().required(),
  organizationSettings: yup.object().shape({
    requiresUserConsent: yup.boolean().required(),
    region: yup.string().oneOf(Object.values(OrganizationRegions)).required(),
    disallowPhi: yup.boolean().required(),
    allowPhiAccessManagement: yup.boolean().required(),
    showLabRecommendationsInRecommendationBuilder: yup.boolean().required(),
    virtualConferenceInfoPanelEnabled: yup.boolean().required(),
    virtualConferenceAudioRecording: yup.boolean().required(),

    autoFillFacilitiesForAccreditation: yup.boolean().required(),
    sendPresentingProviderOnWriteback: yup.boolean().required(),
    newCasePostedEmailAlertsEnabled: yup.boolean().required(),
    dailyReminderPathology: yup.boolean().required(),
    dailyReminderRadiology: yup.boolean().required(),
    dailyReminderGenetics: yup.boolean().required(),
    dailyReminderAdministration: yup.boolean().required(),
    consentText: yup
      .string()
      .trim()
      .when('requiresUserConsent', {
        is: (requiresUserConsent: boolean) => requiresUserConsent,
        then: (schema) => schema.required('Consent Text is required'),
        otherwise: (schema) => schema.nullable(),
      }),
    treatmentPlanDisclaimer: yup.string().trim().nullable(),
    treatmentPlanTemplate: yup.string().trim().nullable(),
    medicalHistoryTemplate: yup.string().trim().nullable(),
  }),
  address: yup.string().nullable(),
  city: yup.string().nullable(),
  state: yup.string().nullable(),
  zipCode: yup.string().nullable(),
  domains: yup.array().of(
    yup.string().matches(/^([A-Za-z0-9-]+\.)+[A-Za-z0-9-]+$/, {
      message: 'Invalid email domain',
    }),
  ),
  salesForceId: yup.string().nullable(),
  // The server validates these only when non-empty, so empty must stay valid here too.
  slug: yup.string().nullable().matches(SLUG_PATTERN, {
    message: 'Use lowercase letters, numbers and single hyphens (e.g. "my-org")',
    excludeEmptyString: true,
  }),
  brandColor: yup.string().nullable().matches(HEX_COLOR_PATTERN, {
    message: 'Use a 6-digit hex color, e.g. #1A73E8',
    excludeEmptyString: true,
  }),
});

const defaultValues = {
  name: '',
  referenceCode: '',
  organizationSettings: {
    requiresUserConsent: false,
    region: OrganizationRegions.US,
    disallowPhi: false,
    allowPhiAccessManagement: false,
    showLabRecommendationsInRecommendationBuilder: false,
    virtualConferenceInfoPanelEnabled: false,
    virtualConferenceAudioRecording: false,

    autoFillFacilitiesForAccreditation: true,
    sendPresentingProviderOnWriteback: false,
    newCasePostedEmailAlertsEnabled: false,
    dailyReminderPathology: false,
    dailyReminderRadiology: false,
    dailyReminderGenetics: false,
    dailyReminderAdministration: false,
    consentText: '',
    treatmentPlanDisclaimer: '',
    treatmentPlanTemplate: '',
    medicalHistoryTemplate: '',
  },
  address: '',
  city: '',
  state: '',
  zipCode: '',
  salesForceId: '',
  domains: [],
  slug: '',
  brandColor: '',
};

/**
 * Reference Code and the Collaborate form URL look interchangeable on this form but are not:
 * only the latter drives `/c/{slug}`. Administration raises the same bare 409 for either
 * collision (see ErrorHandlerMiddleware) with no field named in the body, so attribute the
 * conflict to whichever unique field the admin actually changed.
 */
type ConflictField = 'slug' | 'referenceCode' | 'both';

const resolveConflict = (
  submitted: OrganizationForUpdateDto,
  saved: OrganizationDto | undefined,
): { field: ConflictField; message: string } => {
  const slugChanged = (submitted.slug ?? '') !== (saved?.slug ?? '');
  const referenceCodeChanged = (submitted.referenceCode ?? '') !== (saved?.referenceCode ?? '');

  if (slugChanged && !referenceCodeChanged) {
    return {
      field: 'slug',
      message: 'That Collaborate form URL is already taken by another organization.',
    };
  }
  if (referenceCodeChanged && !slugChanged) {
    return {
      field: 'referenceCode',
      message: 'That Reference Code is already taken by another organization.',
    };
  }
  return {
    field: 'both',
    message:
      'That Reference Code or Collaborate form URL is already taken by another organization. Both must be unique.',
  };
};

export const OrganizationForm = ({ organizationId }: OrganizationFormProps) => {
  const userManagerEnabled = useUserManager();
  const v2ConferenceEmailsEnabled = useV2ConferenceEmails();
  const { data: organization } = useGetOrganization(organizationId);

  const formStore = useFormStore(defaultValues, organizationSchema);
  const [updateStore, isValid, getValues] = formStore(
    (x) => [x.updateStore, x.isValid, x.getValues],
    shallow,
  );
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState<{ field: ConflictField; message: string } | null>(null);
  const navigate = useNavigate();
  const updateOrganizationMutation = useUpdateOrganization(organizationId);

  useEffect(() => {
    if (organization) {
      updateStore((x) => {
        Object.assign(x, organization);
      });
    }
  }, [organization, updateStore]);
  const onSave = () => {
    const store = formStore.getState();

    if (!store.isValid) return;

    setSaving(true);
    setConflict(null);
    const dataToSave = store.getValues();
    updateOrganizationMutation.mutate(dataToSave, {
      onSuccess: () => OncolensToast.success('Organization saved'),
      onError: (error) => {
        if (error.response?.status !== 409) {
          OncolensToast.error('An error occurred');
          return;
        }
        const duplicate = resolveConflict(dataToSave, organization);
        setConflict(duplicate);
        OncolensToast.error(duplicate.message);
      },
      onSettled: () => setSaving(false),
    });
  };

  const { data: hasRekkyLicense } = useOrganizationHasLicense(
    organizationId,
    Licenses.MolecularRecommendations,
  );

  const { data: hasVirtualConferenceLicense } = useOrganizationHasLicense(
    organizationId,
    Licenses.VirtualConference,
  );

  // Region is configured on top-level (parent) organizations only — those parented
  // directly to the OncoLens root org (or with no parent). Child orgs inherit the
  // value and see it read-only.
  const isTopLevelOrganization =
    !!organization &&
    (!organization.parentOrganizationId ||
      organization.parentOrganizationId === OncoLensRootOrganizationId);

  // Child orgs render the Region read-only and inherit their parent's value. Fetch the
  // parent-resolved effective region so the disabled dropdown DISPLAYS the inherited value
  // the adjacent tooltip promises — not the child's own raw stored value. Enabled for
  // children only; top-level orgs edit their raw value directly and need no lookup.
  const { data: effectiveRegion } = useGetEffectiveRegion(
    organizationId,
    !!organization && !isTopLevelOrganization,
  );

  return (
    <div className="rounded-md bg-white p-3 min-h-[32rem] ring-opacity-60 focus:outline-none">
      <div className="grid grid-cols-1 mb-4 gap-y-6 sm:grid-cols-6 sm:gap-x-6">
        <div className="col-span-6 md:col-span-3">
          <FormField name="name" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextInput
                id="name"
                type="text"
                htmlName="name"
                label="Name"
                error={errorMessage}
                value={value}
                onChange={onChange}
                disabled={!!organizationId}
              />
            )}
          </FormField>
          <FormField name="referenceCode" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextInput
                id="organization.referenceCode"
                type="text"
                htmlName="referenceCode"
                label="Reference Code"
                error={errorMessage}
                value={value}
                onChange={onChange}
                // Pinned rather than left to TextInput's label-derived default, which Sauron's
                // CHANCELLOR_ORG_CODE ([data-cy=reference-code]) currently depends on.
                testSelector="reference-code"
                wrapperProps={{ className: 'mt-4' }}
                tooltip={{
                  text: 'Identifies this org to EMR and data integrations. Changing it can break them.',
                  icon: <FaInfoCircle className="ml-1.5 text-blue-400 cursor-help" />,
                  testSelector: 'reference-code-tooltip',
                }}
              />
            )}
          </FormField>
          {conflict && conflict.field !== 'slug' && (
            <p className="mt-1 text-sm text-red-600" data-cy="reference-code-conflict-error">
              {conflict.message}
            </p>
          )}
          <FormField name="salesForceId" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextInput
                id="organization.salesForceId"
                type="text"
                htmlName="salesForceId"
                label="SalesForce ID"
                error={errorMessage}
                value={value}
                onChange={onChange}
                wrapperProps={{ className: 'mt-4' }}
              />
            )}
          </FormField>
          <FormField name="address" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextAreaInput
                id="organization.address"
                label="Address"
                value={value}
                onChange={onChange}
                htmlName="address"
                rows={2}
                error={errorMessage}
                wrapperProps={{ className: 'mt-4' }}
              />
            )}
          </FormField>
          <div className="grid grid-cols-3 mt-4 gap-x-4">
            <FormField name="city" store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <TextInput
                  id="organization.city"
                  label="City"
                  value={value}
                  onChange={onChange}
                  type="text"
                  htmlName="city"
                  error={errorMessage}
                />
              )}
            </FormField>
            <FormField name="state" store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <TextInput
                  id="organization.state"
                  label="State"
                  value={value}
                  onChange={onChange}
                  type="text"
                  htmlName="state"
                  error={errorMessage}
                />
              )}
            </FormField>
            <FormField name="zipCode" store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <TextInput
                  id="organization.zipCode"
                  label="Zip Code"
                  value={value}
                  onChange={onChange}
                  type="text"
                  htmlName="zipCode"
                  error={errorMessage}
                />
              )}
            </FormField>
          </div>
          {userManagerEnabled && (
            <div className="mt-4">
              <FormField name="domains" store={formStore}>
                {({ value, onChange }) => (
                  <>
                    <DomainMultiSelect values={value ?? []} onChange={onChange} />
                  </>
                )}
              </FormField>
            </div>
          )}
          <FormField name="organizationSettings.treatmentPlanTemplate" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextAreaInput
                id="organization.organizationSettings.treatmentPlanTemplate"
                label="Treatment Plan Template"
                value={value}
                onChange={onChange}
                htmlName="treatmentPlanTemplate"
                rows={8}
                wrapperProps={{ className: 'mt-4' }}
                error={errorMessage}
              />
            )}
          </FormField>
          <FormField name="organizationSettings.treatmentPlanDisclaimer" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextAreaInput
                id="organization.organizationSettings.treatmentPlanDisclaimer"
                label="Treatment Plan Disclaimer"
                value={value}
                onChange={onChange}
                htmlName="treatmentPlanDisclaimer"
                rows={8}
                wrapperProps={{ className: 'mt-4' }}
                error={errorMessage}
              />
            )}
          </FormField>
          <FormField name="organizationSettings.medicalHistoryTemplate" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <TextAreaInput
                id="organization.organizationSettings.medicalHistoryTemplate"
                label="Medical History Template"
                value={value}
                onChange={onChange}
                htmlName="medicalHistoryTemplate"
                rows={8}
                wrapperProps={{ className: 'mt-4' }}
                error={errorMessage}
                data-cy="medical-history-template-input"
              />
            )}
          </FormField>
        </div>
        <div className="col-span-3">
          {/* Logo, form URL and brand color are read by exactly one consumer — the anonymous
              Collaborate form shell, via the org branding endpoint — so they are grouped here
              rather than mixed in with the org's general settings. */}
          <div>
            <div className="flex items-center mb-1">
              <h3 className="text-base font-semibold text-gray-800" data-cy="collaborate-header">
                Collaborate
              </h3>
            </div>
            <p className="mb-3 text-xs text-gray-500">
              How this organization appears on its public Collaborate form.
            </p>
            {organizationId && (
              <OrganizationLogoUpload
                organizationId={organizationId}
                currentLogoUrl={
                  organization?.logoS3Key ? `/api/organizations/${organizationId}/logo` : undefined
                }
              />
            )}
            <FormField name="slug" store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <>
                  <TextInput
                    id="organization.slug"
                    type="text"
                    htmlName="slug"
                    label="Collaborate form URL"
                    placeholder="my-org"
                    error={errorMessage}
                    value={value ?? ''}
                    onChange={onChange}
                    testSelector="collaborate-form-url-input"
                    wrapperProps={{ className: 'mt-4' }}
                    tooltip={{
                      text: "Public web address for this org's Collaborate form.",
                      icon: <FaInfoCircle className="ml-1.5 text-blue-400 cursor-help" />,
                      testSelector: 'collaborate-form-url-tooltip',
                    }}
                  />
                  {/* Path only: Chancellor has no environment-aware host for the V2 app, and
                      hardcoding one would be wrong in prod/beta/EU/SA. */}
                  <p
                    className="mt-1 font-mono text-xs text-gray-600"
                    data-cy="collaborate-form-url-preview">
                    {`/c/${value || 'your-organization'}/your-form`}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Lowercase letters, numbers and hyphens. Used in links you share with referrers.
                  </p>
                </>
              )}
            </FormField>
            {conflict && conflict.field !== 'referenceCode' && (
              <p
                className="mt-1 text-sm text-red-600"
                data-cy="collaborate-form-url-conflict-error">
                {conflict.message}
              </p>
            )}
            <FormField name="brandColor" store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <div className="flex items-end mt-4 gap-x-2">
                  <input
                    type="color"
                    aria-label="Brand color picker"
                    // The native picker emits lowercase hex, which the backend accepts as-is.
                    value={value && HEX_COLOR_PATTERN.test(value) ? value : UNSET_SWATCH_COLOR}
                    onChange={onChange}
                    data-cy="brand-color-swatch"
                    className="flex-shrink-0 w-10 h-9 border rounded-md cursor-pointer border-oncolensOrange-500"
                  />
                  <TextInput
                    id="organization.brandColor"
                    type="text"
                    htmlName="brandColor"
                    label="Brand color"
                    placeholder="#1A73E8"
                    error={errorMessage}
                    value={value ?? ''}
                    onChange={onChange}
                    testSelector="brand-color-input"
                    wrapperProps={{ className: 'flex-1' }}
                  />
                </div>
              )}
            </FormField>
          </div>
          <div className="flex items-center mt-4 mb-3">
            <h3 className="text-base font-semibold text-gray-800" data-cy="settings-header">
              Settings
            </h3>
          </div>
          <div className="mb-4">
            <FormField name="organizationSettings.region" store={formStore}>
              {({ value, onChange, errorMessage }) => {
                // Display-only override for child orgs: show the parent-resolved effective
                // region rather than the raw stored value. Never written to the form store,
                // so the submitted payload is exactly what it was before. Fail closed — while
                // the effective region is loading or errored, fall back to the raw value.
                const displayedRegion =
                  !isTopLevelOrganization && effectiveRegion ? effectiveRegion : value;

                return (
                  <Listbox
                    label="Region"
                    data={regionOptions}
                    selected={displayedRegion}
                    setSelected={(val) => onChange(val)}
                    required
                    skipSort
                    disabled={!isTopLevelOrganization}
                    error={errorMessage}
                    testSelector="organization-region"
                    tooltip={{
                      text: 'Set on the parent organization only. Child organizations inherit this value.',
                      icon: <FaInfoCircle className="ml-1.5 text-blue-400 cursor-help" />,
                      testSelector: 'organization-region-tooltip',
                    }}
                  />
                );
              }}
            </FormField>
          </div>
          <div className="flex items-center mb-2">
            <span className="text-sm font-medium text-gray-700" data-cy="phi-settings-label">
              PHI
            </span>
          </div>
          <div className="flex items-center">
            <FormField name="organizationSettings.disallowPhi" store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <>
                  <Checkbox
                    id="organization.organizationSettings.disallowPhi"
                    htmlName="disallowPhi"
                    label="Disallow Phi"
                    value={value || false}
                    onClick={onChange}
                    disabled
                  />
                  {errorMessage ? (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                  ) : null}
                </>
              )}
            </FormField>
            <Tooltip
              text="De-identifies all PHI for this organization — PHI is hidden for every user at the org"
              icon={<FaInfoCircle className="ml-1.5 text-blue-400 cursor-help" />}
              wrapperClassName="mb-2"
              data-cy="disallow-phi-tooltip"
            />
          </div>
          <div className="flex items-center">
            <FormField name="organizationSettings.allowPhiAccessManagement" store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <>
                  <Checkbox
                    id="organization.organizationSettings.allowPhiAccessManagement"
                    htmlName="allowPhiAccessManagement"
                    label="Allow Admin to turn on PHI visibility"
                    value={value || false}
                    onClick={onChange}
                    data-cy="allow-phi-access-management-checkbox"
                  />
                  {errorMessage ? (
                    <p
                      className="mt-2 text-sm text-red-600"
                      data-cy="allow-phi-access-management-error">
                      {errorMessage}
                    </p>
                  ) : null}
                </>
              )}
            </FormField>
            <Tooltip
              text="Lets a PHI Manager grant PHI visibility to individual users at this organization"
              icon={<FaInfoCircle className="ml-1.5 text-blue-400 cursor-help" />}
              wrapperClassName="mb-2"
              data-cy="allow-phi-access-management-tooltip"
            />
          </div>
          <FormField name="organizationSettings.requiresUserConsent" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <>
                <Checkbox
                  id="organization.organizationSettings.requiresUserConsent"
                  htmlName="requiresUserConsent"
                  label="Require User Consent"
                  value={value || false}
                  onClick={onChange}
                />
                {errorMessage ? <p className="mt-2 text-sm text-red-600">{errorMessage}</p> : null}
              </>
            )}
          </FormField>
          <FormField
            name="organizationSettings.consentText"
            store={formStore}
            validationOptions={{
              validate: true,
              valueProvider: getValues,
            }}>
            {({ value, onChange, errorMessage }) => (
              <TextAreaInput
                id="organization.organizationSettings.consentText"
                label="Consent Text"
                value={value}
                onChange={onChange}
                htmlName="consentText"
                rows={8}
                wrapperProps={{ className: 'col-span-6 md:col-span-3 mt-2' }}
                error={errorMessage}
              />
            )}
          </FormField>
          <div className="flex items-center mt-4 mb-2">
            <span className="text-sm font-medium text-gray-700" data-cy="features-settings-label">
              Features
            </span>
          </div>
          <FormField
            name="organizationSettings.autoFillFacilitiesForAccreditation"
            store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <>
                <Checkbox
                  id="organization.organizationSettings.autoFillFacilitiesForAccreditation"
                  htmlName="autoFillFacilitiesForAccreditation"
                  label="Auto-fill Facilities for Accreditation"
                  value={value || false}
                  onClick={onChange}
                  data-cy="auto-fill-facilities-for-accreditation-checkbox"
                />
                {errorMessage ? (
                  <p
                    className="mt-2 text-sm text-red-600"
                    data-cy="auto-fill-facilities-for-accreditation-error">
                    {errorMessage}
                  </p>
                ) : null}
              </>
            )}
          </FormField>
          {hasRekkyLicense && (
            <FormField
              name="organizationSettings.showLabRecommendationsInRecommendationBuilder"
              store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <>
                  <Checkbox
                    id="organization.organizationSettings.showLabRecommendationsInRecommendationBuilder"
                    htmlName="showLabRecommendationsInRecommendationBuilder"
                    label="Show Lab Recommendations in Recommendation Builder"
                    value={value || false}
                    onClick={onChange}
                  />
                  {errorMessage ? (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                  ) : null}
                </>
              )}
            </FormField>
          )}
          <div className="mt-4">
            <div className="flex items-center mb-2">
              <span
                className="text-sm font-medium text-gray-700"
                data-cy="virtual-conference-settings-label">
                Virtual Conference Settings
              </span>
              <Tooltip
                text="Enable the Virtual Conference license to configure these settings"
                icon={<FaInfoCircle className="ml-1.5 text-blue-400 cursor-help" />}
                data-cy="virtual-conference-settings-tooltip"
              />
            </div>
            <FormField
              name="organizationSettings.virtualConferenceInfoPanelEnabled"
              store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <>
                  <Checkbox
                    id="organization.organizationSettings.virtualConferenceInfoPanelEnabled"
                    htmlName="virtualConferenceInfoPanelEnabled"
                    label="Allow admins to see the meeting info panel"
                    value={value || false}
                    onClick={onChange}
                    disabled={!hasVirtualConferenceLicense}
                  />
                  {errorMessage ? (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                  ) : null}
                </>
              )}
            </FormField>
            <FormField
              name="organizationSettings.virtualConferenceAudioRecording"
              store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <>
                  <Checkbox
                    id="organization.organizationSettings.virtualConferenceAudioRecording"
                    htmlName="virtualConferenceAudioRecording"
                    label="Audio recording"
                    value={value || false}
                    onClick={onChange}
                    disabled={!hasVirtualConferenceLicense}
                  />
                  {errorMessage ? (
                    <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                  ) : null}
                </>
              )}
            </FormField>
          </div>
          <div className="mt-4">
            <span className="text-sm font-medium text-gray-700" data-cy="writeback-settings-label">
              Writeback Settings
            </span>
            <FormField
              name="organizationSettings.sendPresentingProviderOnWriteback"
              store={formStore}>
              {({ value, onChange, errorMessage }) => (
                <>
                  <Checkbox
                    id="organization.organizationSettings.sendPresentingProviderOnWriteback"
                    htmlName="sendPresentingProviderOnWriteback"
                    label="Send Presenting Provider (instead of sent by user)"
                    value={value || false}
                    onClick={onChange}
                    data-cy="send-presenting-provider-on-writeback-checkbox"
                  />
                  {errorMessage ? (
                    <p
                      className="mt-2 text-sm text-red-600"
                      data-cy="send-presenting-provider-on-writeback-error">
                      {errorMessage}
                    </p>
                  ) : null}
                </>
              )}
            </FormField>
          </div>
          {v2ConferenceEmailsEnabled && (
            <div className="mt-4" data-cy="notification-settings-section">
              <span
                className="text-sm font-medium text-gray-700"
                data-cy="notification-settings-label">
                Notification Settings
              </span>
              <FormField
                name="organizationSettings.newCasePostedEmailAlertsEnabled"
                store={formStore}>
                {({ value, onChange }) => (
                  <Checkbox
                    id="organization.organizationSettings.newCasePostedEmailAlertsEnabled"
                    htmlName="newCasePostedEmailAlertsEnabled"
                    label="Enable new case posted email alerts"
                    value={value || false}
                    onClick={onChange}
                    data-cy="new-case-posted-email-alerts-checkbox"
                  />
                )}
              </FormField>
              <div className="mt-3">
                <span className="text-sm font-medium text-gray-700" data-cy="daily-reminders-label">
                  Daily Email Reminders
                </span>
                <div className="flex flex-col">
                  <FormField name="organizationSettings.dailyReminderPathology" store={formStore}>
                    {({ value, onChange }) => (
                      <Checkbox
                        id="organization.organizationSettings.dailyReminderPathology"
                        htmlName="dailyReminderPathology"
                        label="Pathology"
                        value={value || false}
                        onClick={onChange}
                        data-cy="daily-reminder-pathology-checkbox"
                      />
                    )}
                  </FormField>
                  <FormField name="organizationSettings.dailyReminderRadiology" store={formStore}>
                    {({ value, onChange }) => (
                      <Checkbox
                        id="organization.organizationSettings.dailyReminderRadiology"
                        htmlName="dailyReminderRadiology"
                        label="Radiology"
                        value={value || false}
                        onClick={onChange}
                        data-cy="daily-reminder-radiology-checkbox"
                      />
                    )}
                  </FormField>
                  <FormField name="organizationSettings.dailyReminderGenetics" store={formStore}>
                    {({ value, onChange }) => (
                      <Checkbox
                        id="organization.organizationSettings.dailyReminderGenetics"
                        htmlName="dailyReminderGenetics"
                        label="Genetics"
                        value={value || false}
                        onClick={onChange}
                        data-cy="daily-reminder-genetics-checkbox"
                      />
                    )}
                  </FormField>
                  <FormField
                    name="organizationSettings.dailyReminderAdministration"
                    store={formStore}>
                    {({ value, onChange }) => (
                      <Checkbox
                        id="organization.organizationSettings.dailyReminderAdministration"
                        htmlName="dailyReminderAdministration"
                        label="Administration"
                        value={value || false}
                        onClick={onChange}
                        data-cy="daily-reminder-administration-checkbox"
                      />
                    )}
                  </FormField>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center w-full">
        <Button
          className="w-auto px-6 ml-auto mr-3"
          buttonColor="gray"
          onClick={() => navigate(-1)}>
          Back
        </Button>
        {organization && <LegacyOrgLink organization={organization} />}
        <ButtonWithLoading
          loading={saving}
          className="w-auto px-6"
          buttonColor="blue"
          onClick={onSave}
          disabled={!isValid}>
          Save
        </ButtonWithLoading>
      </div>
    </div>
  );
};

interface DomainMultiSelectProps {
  values: string[];
  onChange: (newValues: string[]) => void;
}

function DomainMultiSelect({ values, onChange }: DomainMultiSelectProps) {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function validateDomains(newValues: string[]) {
    try {
      await organizationSchema.fields.domains.validate(newValues);
      setErrorMessage(null);
      onChange(newValues);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrorMessage(error.message);
      }
    }
  }

  function handleDomainEvent() {
    if (inputValue.trim()) {
      const newDomain = inputValue.trim();
      if (!values.includes(newDomain)) {
        validateDomains([...values, newDomain]);
      }
      setInputValue('');
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === 'Tab') {
      handleDomainEvent();
      e.preventDefault();
    }
  }

  function removeValue(valueToRemove: string) {
    validateDomains(values.filter((v) => v !== valueToRemove));
  }

  return (
    <div className="w-full gap-2 col-span-full ">
      <div className="flex flex-wrap items-center w-full">
        <TextInput
          placeholder="Enter email domain"
          id="domains"
          htmlName="domains"
          type="text"
          label="Email domains"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          inputProps={{
            onKeyDown: handleKeyDown,
          }}
          onBlur={handleDomainEvent}
          wrapperProps={{ className: 'col-span-full w-full' }}
        />
      </div>
      <div className="pt-2">
        {errorMessage && <p className="mb-2 text-sm text-rose-600">{errorMessage}</p>}
        <div className="flex space-x-2 ">
          {values.map((value) => (
            <span
              key={value}
              className="flex items-center h-8 px-3 py-2 text-sm font-medium text-white rounded-sm shadow select-none bg-oncolensBlue-500 border:blue-800 hover:bg-oncolensBlue-500/80">
              {value}
              <button
                type="button"
                onClick={() => removeValue(value)}
                className="p-0.5 -mr-1 ml-1 text-white">
                <XIcon className="w-4 h-4" />
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
